// Translations for STC Marine Services — EN / ES.

const en = {
  nav: {
    home: 'Home',
    services: 'Services',
    about: 'About',
    why: 'Why Us',
    contact: 'Contact',
    cta: 'Enquire Now',
  },
  hero: {
    eyebrow: 'Maritime Consultants',
    title: 'Marine Surveys & Audits, executed with precision.',
    subtitle:
      'STC Marine Services delivers independent surveys, vetting preparation and compliance audits for owners, operators, charterers and insurers — worldwide.',
    ctaPrimary: 'Enquire Now',
    ctaSecondary: 'View Services',
  },
  about: {
    eyebrow: 'About the Firm',
    title: 'Decades at sea, distilled into measurable assurance.',
    body1:
      'STC Marine Services brings a master mariner’s judgement to every attendance. We work for shipowners, P&I clubs, charterers, and underwriters who need clarity — fast, defensible reports with photographic evidence and actionable findings.',
    body2:
      'Our work is independent, discreet and built on direct seagoing experience: tanker, dry bulk, container and offshore. We attend at major ports worldwide. Remote inspections and audits are also performed in line with OCIMF guidelines.',
    founderLabel: 'Founder & Principal Auditor',
    founderName: 'Capt. Stylianos Chatzigiakoumis',
    chips: ['Master Mariner-led team', 'Worldwide attendance', 'OCIMF-aligned remote audits'],
  },
  services: {
    eyebrow: 'Services',
    title: 'A complete maritime assurance practice.',
    subtitle:
      'From pre-purchase due diligence to PSC attendance and remote VDR audits, the practice covers the full lifecycle of vessel risk.',
    viewDetails: 'View all services',
    items: {
      'condition-prepurchase': {
        name: 'Condition & Pre-Purchase Surveys',
        short: 'Independent due diligence before you sign.',
        long: 'Full hull, machinery, accommodation and documentation review with a defensible condition rating, market commentary and a photographic dossier — supporting purchase, sale and refinancing decisions.',
      },
      damage: {
        name: 'Damage Surveys',
        short: 'Scope, cause and cost of damage, on the record.',
        long: 'Casualty attendance to determine extent, root cause and recommended repairs, with class and insurer liaison. Reports built to withstand recovery and subrogation review.',
      },
      'draft-bunker': {
        name: 'Draft & Bunker Surveys',
        short: 'Quantities you can defend in any dispute.',
        long: 'Precise draft surveys for cargo quantity determination and bunker quantity surveys before, during and after transfer — protecting against short-loading and supplier disputes.',
      },
      'on-off-hire': {
        name: 'On / Off-Hire Surveys',
        short: 'Clean handovers between owner and charterer.',
        long: 'Detailed condition, ROB bunker and lube oil quantification at delivery and redelivery, fully reconciled to the charter party.',
      },
      'ism-isps-mlc': {
        name: 'ISM / ISPS / MLC Audits — Internal & External',
        short: 'Compliance audits that hold up to flag and PSC scrutiny.',
        long: 'Internal and external audits against the ISM Code, ISPS Code and MLC 2006. Gap analysis, corrective action plans, on-site verification and report packaging suitable for flag and class review.',
      },
      'pre-vetting-sire-cdi': {
        name: 'Pre-Vetting Inspection Audits (SIRE / CDI)',
        short: 'Walk into the inspection ready.',
        long: 'Mock SIRE 2.0 and CDI inspections, MOC review, document readiness, crew interview drills, hardware verification and finding remediation in advance of the actual inspector.',
      },
      'psc-inspection': {
        name: 'PSC Inspection Preparation & Attendance',
        short: 'Reduce detentions. Defend findings.',
        long: 'Pre-arrival readiness reviews and on-board attendance during Port State Control inspections, with real-time advisory and post-inspection corrective action management.',
      },
      'remote-vdr': {
        name: 'Remote VDR Audits',
        short: 'Voyage analytics without leaving port.',
        long: 'Secure remote analysis of VDR data and bridge audio/video to assess navigational practice, BRM, incident reconstruction and crew compliance — delivered as a structured findings report.',
      },
      'navigation-audit': {
        name: 'Navigation Audits & Assessments',
        short: 'Bridge-team performance, measured.',
        long: 'Sea-going or in-port assessment of passage planning, bridge resource management, ECDIS use, watchkeeping standards and lookout discipline against ISM and company SMS requirements.',
      },
      'blending-services': {
        name: 'Blending Services',
        short: 'Supervised in-tank and in-line blending for the oil industry.',
        long: 'Attendance and supervision of crude and product blending operations — in-tank, in-line and on-board. We verify component quantities, sequence and homogenisation, take representative samples and witness on-spec certification so the delivered product meets the buyer’s specification.',
      },
      'loss-control-expediting': {
        name: 'Loss Control & Expediting',
        short: 'On-the-spot supervision of loading and discharge for oil cargoes.',
        long: 'Attendance of loading or discharge operations by qualified loss-control professionals: line-up verification, draft and ullage measurements, sampling, ROB inspection, line displacement calculations and expediting to minimise port-time, demurrage exposure and cargo loss.',
      },
      'general-inquiries': {
        name: 'General Inquiries',
        short: 'Not sure which service — let’s talk.',
        long: 'For general enquiries, partnerships, training requests or anything not covered by a specific service line.',
      },
    },
  },
  why: {
    eyebrow: 'Why STC',
    title: 'Independent. Direct. Defensible.',
    items: [
      {
        title: 'Master Mariner-led team',
        body: 'Every assignment is led by a senior master mariner and delivered by a vetted team of attending auditors and surveyors — not a one-person practice.',
      },
      {
        title: 'Worldwide attendance',
        body: 'Mobilisation worldwide through our team and a vetted correspondent network — major ports, terminals and anchorages on short notice.',
      },
      {
        title: 'Defensible reporting',
        body: 'Reports are concise, photographic, time-stamped and built to be read by class, flag, insurers and counsel.',
      },
      {
        title: 'Confidential by default',
        body: 'NDA-friendly engagements. Findings are shared only with the parties you authorise.',
      },
    ],
    stats: [
      { value: '20+', label: 'Years Sea Experience' },
      { value: '11', label: 'Service Lines' },
      { value: '24/7', label: 'Casualty Response' },
      { value: 'Worldwide', label: 'Coverage' },
    ],
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Frequently asked questions.',
    subtitle:
      'Clear answers about marine audits, SIRE 2.0 and CDI vetting preparation, PSC attendance and remote VDR analysis.',
    items: [
      {
        q: 'Do you perform ISM, ISPS and MLC 2006 audits?',
        a: 'STC performs ISM, ISPS and MLC 2006 audits — internal and external — to verify and reinforce compliance with the company\'s SMS. Engagements include gap analysis, corrective action plans and reports suitable for flag and class review.',
      },
      {
        q: 'How do you prepare a tanker for a SIRE 2.0 inspection?',
        a: 'Full preparation for the SIRE 2.0 inspection on board, aligned with the latest OCIMF question set — management of change review, document readiness, hardware verification, crew interview preparation and a written remediation plan. The vessel walks into the actual inspection with known weaknesses already closed out.',
      },
      {
        q: 'Do you cover CDI inspections for bulk carriers?',
        a: 'Yes. STC prepares bulk carriers for CDI inspection — documentation review, hardware and operational readiness, crew briefing and remediation of identified gaps prior to the inspector\'s attendance.',
      },
      {
        q: 'Do you attend PSC inspections worldwide?',
        a: 'Yes. We attend Port State Control inspections worldwide through our direct team and a vetted correspondent network. Pre-arrival readiness reviews, on-board advisory during the inspection and post-inspection corrective action — built to reduce detentions and defend findings.',
      },
      {
        q: 'What does a remote VDR audit cover?',
        a: 'A secure off-site analysis of bridge audio, video, radar, AIS and sensor data to assess navigational practice, Bridge Resource Management, watchkeeping standards and incident reconstruction — delivered as a structured findings report aligned with the SMS and ISM Code.',
      },
      {
        q: 'Are your audits aligned with OCIMF guidelines?',
        a: 'Yes. Pre-vetting, navigation and remote audits are conducted in line with OCIMF guidance — SIRE 2.0, TMSA and the OCIMF remote inspection framework. Reports are structured to be acceptable to oil-major vetting departments and insurers.',
      },
      {
        q: 'What areas and major port hubs do you cover?',
        a: 'Worldwide attendance at major port hubs — Mediterranean, North Europe, Black Sea, Middle East Gulf, Far East, United States and Central America. Coverage is delivered through our direct team and a vetted correspondent network.',
      },
      {
        q: 'How quickly can you mobilise for an urgent attendance?',
        a: 'STC Marine Services is available for urgent attendance as may be required, mobilising through our direct team and vetted correspondent network to attend at major ports worldwide.',
      },
      {
        q: 'Do you supervise oil blending operations?',
        a: 'Yes. STC supervises oil blending as required by the charterer\'s loading instructions — component verification, sequence and homogenisation control, representative sampling and witnessing of on-spec certification before delivery.',
      },
      {
        q: 'What is covered under loss control and expediting?',
        a: 'On-site supervision of cargo loading and discharge by qualified loss-control professionals — line-up verification, draft and ullage measurements, sampling, ROB inspection, line displacement calculations and active expediting to minimise port-time, demurrage exposure and cargo loss.',
      },
      {
        q: 'Who leads STC Marine Services?',
        a: 'STC Marine Services was founded and is led by Capt. Stylianos Chatzigiakoumis, a master mariner with over two decades of seagoing and shore-side experience. Every engagement is led by a senior master mariner and delivered by a vetted team of attending auditors and surveyors, in collaboration with technical and engineering teams as required.',
      },
    ],
  },
  contact: {
    eyebrow: 'Contact',
    title: 'Tell us about your vessel.',
    subtitle:
      'Share a few details and our team will respond promptly. For casualty and urgent attendance, please call directly.',
    name: 'Full name',
    email: 'Email',
    company: 'Company / Vessel',
    service: 'Service of interest',
    servicePlaceholder: 'Select a service',
    message: 'Message',
    messagePlaceholder: 'Vessel type, location, IMO number, and the assistance you need…',
    submit: 'Send enquiry',
    submitting: 'Sending…',
    success: 'Thank you — your enquiry has been received. We will respond shortly.',
    error: 'Sorry, something went wrong. Please try again or email us directly.',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
  },
  servicesPage: {
    eyebrow: 'Service Catalogue',
    title: 'Marine surveys and maritime audits — in depth.',
    subtitle:
      'A line-by-line overview of every engagement type STC Marine Services delivers. Contact us for a tailored scope.',
    ctaTitle: 'Ready to schedule a survey or audit?',
    ctaBody:
      'Tell us the vessel, location and timing. We respond within hours and mobilise immediately for casualty cases.',
    ctaButton: 'Schedule now',
    backHome: 'Back to home',
  },
  footer: {
    tagline: 'Maritime consultants — Marine surveys & Audits.',
    rights: 'All rights reserved.',
    quickLinks: 'Quick links',
    contactHeading: 'Get in touch',
  },
};

const es = {
  nav: {
    home: 'Inicio',
    services: 'Servicios',
    about: 'Nosotros',
    why: 'Por qué',
    contact: 'Contacto',
    cta: 'Consultar ahora',
  },
  hero: {
    eyebrow: 'Consultores Marítimos',
    title: 'Inspecciones y auditorías marinas, con precisión.',
    subtitle:
      'STC Marine Services ofrece inspecciones independientes, preparación de vetting y auditorías de cumplimiento para armadores, operadores, fletadores y aseguradores — en todo el mundo.',
    ctaPrimary: 'Consultar ahora',
    ctaSecondary: 'Ver servicios',
  },
  about: {
    eyebrow: 'Sobre la firma',
    title: 'Décadas en el mar, traducidas en aseguramiento medible.',
    body1:
      'STC Marine Services aporta el criterio de un capitán de altura a cada asistencia. Trabajamos para armadores, P&I clubs, fletadores y aseguradores que necesitan claridad — informes rápidos y defendibles con evidencia fotográfica y hallazgos accionables.',
    body2:
      'Nuestro trabajo es independiente, discreto y basado en experiencia directa de navegación: tanqueros, graneleros, portacontenedores y offshore. Asistimos en los principales puertos a nivel mundial. Las inspecciones y auditorías remotas se realizan conforme a las directrices OCIMF.',
    founderLabel: 'Fundador y Auditor principal',
    founderName: 'Capt. Stylianos Chatzigiakoumis',
    chips: ['Equipo liderado por capitán de altura', 'Cobertura mundial', 'Auditorías remotas OCIMF-aligned'],
  },
  services: {
    eyebrow: 'Servicios',
    title: 'Una práctica completa de aseguramiento marítimo.',
    subtitle:
      'Desde due diligence pre-compra hasta asistencia PSC y auditorías VDR remotas, cubrimos todo el ciclo de vida del riesgo del buque.',
    viewDetails: 'Ver todos los servicios',
    items: {
      'condition-prepurchase': {
        name: 'Inspecciones de Condición y Pre-Compra',
        short: 'Due diligence independiente antes de firmar.',
        long: 'Revisión completa del casco, maquinaria, alojamientos y documentación con calificación defendible y dossier fotográfico.',
      },
      damage: {
        name: 'Inspecciones de Daños',
        short: 'Alcance, causa y coste del daño, en informe.',
        long: 'Asistencia ante siniestro para determinar alcance, causa raíz y reparaciones recomendadas, con enlace a clase y asegurador.',
      },
      'draft-bunker': {
        name: 'Inspecciones de Calado y Combustible',
        short: 'Cantidades defendibles ante cualquier disputa.',
        long: 'Inspecciones de calado precisas y mediciones de bunker antes, durante y después de la transferencia.',
      },
      'on-off-hire': {
        name: 'Inspecciones On / Off-Hire',
        short: 'Entregas limpias entre armador y fletador.',
        long: 'Condición detallada, ROB de combustibles y lubricantes en entrega y redelivery, reconciliado con el charter party.',
      },
      'ism-isps-mlc': {
        name: 'Auditorías ISM / ISPS / MLC — Internas y Externas',
        short: 'Auditorías que resisten el escrutinio de bandera y PSC.',
        long: 'Auditorías internas y externas conforme al ISM, ISPS y MLC 2006. Análisis de brechas, acciones correctivas y verificación in situ.',
      },
      'pre-vetting-sire-cdi': {
        name: 'Pre-Vetting Inspection Audits (SIRE / CDI)',
        short: 'Llegue listo a la inspección.',
        long: 'Inspecciones simuladas SIRE 2.0 y CDI, revisión MOC, preparación documental, drills de tripulación y remediación previa.',
      },
      'psc-inspection': {
        name: 'Preparación y Asistencia PSC',
        short: 'Reduzca detenciones. Defienda hallazgos.',
        long: 'Revisión de preparación previa al arribo y asistencia a bordo durante inspecciones Port State Control, con asesoría en tiempo real.',
      },
      'remote-vdr': {
        name: 'Auditorías VDR Remotas',
        short: 'Analítica del viaje sin salir del puerto.',
        long: 'Análisis remoto seguro de datos VDR y audio/video del puente para evaluar la práctica náutica, BRM y reconstrucción de incidentes.',
      },
      'navigation-audit': {
        name: 'Auditorías y Evaluaciones de Navegación',
        short: 'Rendimiento del equipo de puente, medido.',
        long: 'Evaluación en navegación o puerto del planning, BRM, uso de ECDIS y estándares de guardia frente al ISM y SMS de la empresa.',
      },
      'blending-services': {
        name: 'Servicios de Blending',
        short: 'Supervisión de blending in-tank e in-line para la industria del petróleo.',
        long: 'Asistencia y supervisión de operaciones de blending de crudo y productos — in-tank, in-line y a bordo. Verificamos cantidades de los componentes, secuencia y homogeneización, tomamos muestras representativas y certificamos la entrega on-spec.',
      },
      'loss-control-expediting': {
        name: 'Loss Control & Expediting',
        short: 'Supervisión in situ de carga y descarga de cargamentos petroleros.',
        long: 'Asistencia durante las operaciones de carga/descarga por profesionales de loss-control: verificación de line-up, mediciones de calado y ullage, muestreo, ROB, cálculos de line displacement y expediting para minimizar tiempo en puerto, demurrage y pérdidas.',
      },
      'general-inquiries': {
        name: 'Consultas generales',
        short: 'No sabe qué servicio — hablemos.',
        long: 'Para consultas generales, alianzas, formaciones o cualquier asunto que no encaje en una línea de servicio específica.',
      },
    },
  },
  why: {
    eyebrow: 'Por qué STC',
    title: 'Independientes. Directos. Defendibles.',
    items: [
      { title: 'Equipo liderado por capitán de altura', body: 'Cada asignación es liderada por un capitán senior y entregada por un equipo verificado de auditores y surveyors — no es una práctica de una sola persona.' },
      { title: 'Cobertura mundial', body: 'Movilización mundial a través de nuestro equipo y red de corresponsales — principales puertos y terminales con poca antelación.' },
      { title: 'Informes defendibles', body: 'Concisos, fotográficos y con sello temporal.' },
      { title: 'Confidencialidad', body: 'Asignaciones bajo NDA, discreción garantizada.' },
    ],
    stats: [
      { value: '20+', label: 'Años de experiencia' },
      { value: '11', label: 'Líneas de servicio' },
      { value: '24/7', label: 'Respuesta a siniestros' },
      { value: 'Mundial', label: 'Cobertura' },
    ],
  },
  faq: {
    eyebrow: 'Preguntas frecuentes',
    title: 'Preguntas frecuentes.',
    subtitle:
      'Respuestas claras sobre auditorías marinas, preparación de vetting SIRE 2.0 y CDI, asistencia PSC y análisis remoto VDR.',
    items: [
      {
        q: '¿Realizan auditorías ISM, ISPS y MLC 2006?',
        a: 'STC realiza auditorías ISM, ISPS y MLC 2006 — internas y externas — para verificar y reforzar el cumplimiento del SMS de la compañía. Incluyen análisis de brechas, planes de acciones correctivas e informes aptos para revisión de bandera y clase.',
      },
      {
        q: '¿Cómo se prepara un tanquero para una inspección SIRE 2.0?',
        a: 'Preparación completa para la inspección SIRE 2.0 a bordo, alineada con el cuestionario OCIMF más reciente — revisión de MOC, preparación documental, verificación de hardware, preparación de entrevistas a la tripulación y un plan de remediación escrito. El buque llega a la inspección real con las debilidades conocidas ya cerradas.',
      },
      {
        q: '¿Cubren inspecciones CDI para graneleros?',
        a: 'Sí. STC prepara graneleros para la inspección CDI — revisión documental, preparación operativa y de hardware, briefing de la tripulación y remediación de brechas identificadas antes de la asistencia del inspector.',
      },
      {
        q: '¿Asisten a inspecciones PSC en todo el mundo?',
        a: 'Sí. Asistimos a inspecciones Port State Control en todo el mundo a través de nuestro equipo directo y una red de corresponsales verificada. Revisión previa al arribo, asesoría a bordo durante la inspección y gestión de acciones correctivas — diseñado para reducir detenciones y defender hallazgos.',
      },
      {
        q: '¿Qué cubre una auditoría VDR remota?',
        a: 'Análisis seguro fuera de sitio del audio, video, radar, AIS y datos de sensores del puente para evaluar la práctica náutica, Bridge Resource Management, estándares de guardia y reconstrucción de incidentes — entregado como un informe estructurado alineado con el SMS y el ISM.',
      },
      {
        q: '¿Sus auditorías están alineadas con las directrices OCIMF?',
        a: 'Sí. Las auditorías pre-vetting, de navegación y remotas se realizan conforme a las directrices OCIMF — SIRE 2.0, TMSA y el marco de inspección remota OCIMF. Los informes son aceptables para los departamentos de vetting de las oil-majors y aseguradoras.',
      },
      {
        q: '¿Qué áreas y principales hubs portuarios cubren?',
        a: 'Asistencia mundial en los principales hubs portuarios — Mediterráneo, Norte de Europa, Mar Negro, Golfo del Medio Oriente, Lejano Oriente, Estados Unidos y América Central. La cobertura se entrega a través de nuestro equipo directo y una red de corresponsales verificada.',
      },
      {
        q: '¿Con qué rapidez pueden movilizarse para una asistencia urgente?',
        a: 'STC Marine Services está disponible para asistencias urgentes según se requiera, movilizando a través de nuestro equipo directo y red de corresponsales verificada para atender en los principales puertos a nivel mundial.',
      },
      {
        q: '¿Supervisan operaciones de blending de petróleo?',
        a: 'Sí. STC supervisa el blending de petróleo según lo requerido por las instrucciones de carga del fletador — verificación de componentes, control de secuencia y homogeneización, muestreo representativo y testificación de la certificación on-spec antes de la entrega.',
      },
      {
        q: '¿Qué cubre loss control y expediting?',
        a: 'Supervisión in situ de operaciones de carga y descarga por profesionales de loss-control: verificación de line-up, mediciones de calado y ullage, muestreo, ROB, cálculos de line displacement y expediting activo para minimizar tiempo en puerto, demurrage y pérdidas de carga.',
      },
      {
        q: '¿Quién lidera STC Marine Services?',
        a: 'STC Marine Services fue fundada y es liderada por el Capt. Stylianos Chatzigiakoumis, capitán de altura con más de dos décadas de experiencia en navegación y oficina. Cada asignación es liderada por un capitán senior y entregada por un equipo verificado de auditores y surveyors, en colaboración con equipos técnicos y de ingeniería según se requiera.',
      },
    ],
  },
  contact: {
    eyebrow: 'Contacto',
    title: 'Cuéntenos sobre su buque.',
    subtitle:
      'Comparta algunos detalles y nuestro equipo responderá a la brevedad. Para urgencias, llame directamente.',
    name: 'Nombre completo',
    email: 'Correo electrónico',
    company: 'Empresa / Buque',
    service: 'Servicio de interés',
    servicePlaceholder: 'Seleccione un servicio',
    message: 'Mensaje',
    messagePlaceholder: 'Tipo de buque, ubicación, IMO y la asistencia que necesita…',
    submit: 'Enviar consulta',
    submitting: 'Enviando…',
    success: 'Gracias — su consulta ha sido recibida. Responderemos a la brevedad.',
    error: 'Lo sentimos, algo salió mal. Intente nuevamente o escríbanos directamente.',
    emailLabel: 'Email',
    phoneLabel: 'Teléfono',
  },
  servicesPage: {
    eyebrow: 'Catálogo de servicios',
    title: 'Inspecciones y auditorías marítimas — en detalle.',
    subtitle: 'Resumen línea por línea de cada tipo de encargo. Contáctenos para un alcance a medida.',
    ctaTitle: '¿Listo para agendar una inspección o auditoría?',
    ctaBody: 'Indique buque, ubicación y plazos. Respondemos en horas y movilizamos de inmediato para siniestros.',
    ctaButton: 'Agendar ahora',
    backHome: 'Volver al inicio',
  },
  footer: {
    tagline: 'Consultores marítimos — Inspecciones y auditorías.',
    rights: 'Todos los derechos reservados.',
    quickLinks: 'Enlaces rápidos',
    contactHeading: 'Contáctenos',
  },
};

export const translations = { en, es };

export const SUPPORTED_LANGS = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'es', label: 'ES', name: 'Español' },
];
